import { useState, useRef, useEffect, useCallback, FormEvent } from 'react';
import { useCreateRollMutation } from '../hooks/useRolls';
import { validateRollName } from '../utils/rollUtils';
import type { Roll } from '../types/roll';
import type { RollApiError } from '../hooks/useRolls';
import styles from './RollPanel.module.css';

export interface RollPanelProps {
  orderId: string;
  onRollCreated: (roll: Roll) => void;
}

export function RollPanel({ orderId, onRollCreated }: RollPanelProps) {
  const [rollName, setRollName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const errorRef = useRef<HTMLParagraphElement>(null);
  const autoDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mutation = useCreateRollMutation(orderId, {
    onRollCreated: (roll: Roll) => {
      setRollName('');
      setError(null);
      setSuccessMessage(`Roll "${roll.roll_name}" created successfully`);
      onRollCreated(roll);
    },
  });

  const isSubmitting = mutation.isPending;

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      autoDismissTimerRef.current = setTimeout(() => {
        setError(null);
      }, 10_000);
      return () => {
        if (autoDismissTimerRef.current) {
          clearTimeout(autoDismissTimerRef.current);
        }
      };
    }
  }, [error]);

  // Focus error message when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  // Clear success message after a short delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5_000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRollName(e.target.value);
    // Clear error as user types
    if (error) setError(null);
  }, [error]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      const validationError = validateRollName(rollName);
      if (validationError) {
        return;
      }

      const rollId = crypto.randomUUID();
      setSuccessMessage(null);

      mutation.mutate(
        { roll_id: rollId, roll_name: rollName.trim() },
        {
          onError: (err: RollApiError) => {
            setError(err.message);
          },
        }
      );
    },
    [rollName, mutation]
  );

  const handleDismissError = useCallback(() => {
    setError(null);
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
    }
  }, []);

  const trimmedName = rollName.trim();
  const isButtonDisabled = trimmedName.length === 0 || isSubmitting;

  return (
    <div className={styles.rollPanel}>
      <form onSubmit={handleSubmit} aria-label="Create a new roll">
        <label htmlFor="roll-name-input" className={styles.label}>
          Roll Name
        </label>
        <div className={styles.inputRow}>
          <input
            id="roll-name-input"
            type="text"
            className={styles.input}
            value={rollName}
            onChange={handleInputChange}
            placeholder="e.g. Kodak Gold 200"
            maxLength={100}
            disabled={isSubmitting}
            aria-describedby={error ? 'roll-panel-error' : undefined}
          />
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isButtonDisabled}
          >
            {isSubmitting && <span className={styles.spinner} aria-hidden="true" />}
            Create Roll
          </button>
        </div>
      </form>

      {error && (
        <p
          id="roll-panel-error"
          ref={errorRef}
          className={styles.error}
          role="alert"
          tabIndex={-1}
        >
          <span>{error}</span>
          <button
            type="button"
            className={styles.dismissButton}
            onClick={handleDismissError}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </p>
      )}

      <div aria-live="polite" className={styles.srOnly}>
        {successMessage}
      </div>
    </div>
  );
}
